import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BusyIndicator } from "../../../shared/base-components/react-loader/busy-indicator/BusyIndicator";
import UserAccessService from "../../../features/UserAccessView/services/UserAccessService";
import {
  ACCESS_TYPE,
  ACTIVE_TABS_KEY,
  NO_PROJECTS_FOUND_PLACEHOLDER,
  TOAST_DEFAULT_ERROR_MESSAGE,
  USER_ACCESS,
  USER_NAME_LABEL,
  WRITE_ACCESS,
} from "../../utils/CommonConstants";
import {
  ButtonContainer,
  Close,
  FlexBlock,
  Label,
  UserEditListOutline,
  UserListWrapper,
  EmptyProjectPlaceholderContainer,
  EmptyProjectPlaceholderContent,
  FlexContainer,
  LoaderWrapperComponent,
} from "../UserAccessListComponent/UserAccessListComponent.styles";
import { UserAccess } from "../UserAccessListComponent/UserAcessListComponent.types";
import { PropelCard } from "../../../styles/PropelCard/PropelCard.styles";
import { Button, Checkbox, message } from "antd";
import { Container } from "../../../shared/base-components/Layouts/Container/Container.styles";

export const UserEditList = () => {
  const [isEverythingLoaded, setIsEverythingLoaded] = useState(false);
  const [userAccessData, setUserAccessData] = useState<UserAccess[]>([]);
  const navigate = useNavigate();
  const { deliverableId } = useParams<{ deliverableId: string }>();
  const projectService = new UserAccessService();

  const fetchUserAccessData = async () => {
    try {
      const response = await projectService.getUserDetailsById<UserAccess[]>(
        Number(deliverableId)
      );

      // Sort the response array with the owner on top
      const sortedResponse = sortOwnerOnTop(response);

      setUserAccessData(sortedResponse);
      setIsEverythingLoaded(true);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    fetchUserAccessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliverableId]);

  const sortOwnerOnTop = (data: UserAccess[]) => {
    const ownerIndex = data.findIndex((access) => access.isOwner);
    if (ownerIndex !== -1) {
      const owner = data[ownerIndex];
      const modifiedData = data.filter((_, index) => index !== ownerIndex);
      return [owner, ...modifiedData];
    }
    return data;
  };

  const getAccessType = (access: UserAccess): string => {
    if (access.isOwner) {
      return "Owner";
    } else if (access.canWrite) {
      return "Write";
    } else if (access.canRead) {
      return "Read";
    } else {
      return "Access Removed";
    }
  };

  const handleCheckboxChange = (id: number): void => {
    setUserAccessData((prevUserAccessData) => {
      return prevUserAccessData.map((access) => {
        if (access.id === id) {
          return {
            ...access,
            canWrite: !access.canWrite,
            canRead: access.canWrite,
          };
        }
        return access;
      });
    });
  };

  const handleRemoveUser = (id: number): void => {
    const updatedUserAccess = userAccessData.map((access) => {
      if (access.id === id) {
        return { ...access, canRead: false, canWrite: false };
      } else {
        return access;
      }
    });
    setUserAccessData(updatedUserAccess);
  };

  const handleSave = async (): Promise<void> => {
    try {
      if (deliverableId) {
        const deliverableIdNumber = Number(deliverableId);
        const responseData = await projectService.updateUserDetailsById<
          string[]
        >(deliverableIdNumber, userAccessData);
        message.success(responseData);
        navigate(
          `/deliverable/user-access/ ${deliverableId}?${ACTIVE_TABS_KEY}=${USER_ACCESS}`
        );
      }
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };
  return (
    <>
      {isEverythingLoaded ? (
        <>
          <Container>
            <Label>
              <li className="user">{USER_NAME_LABEL}</li>
              <li>{ACCESS_TYPE}</li>
              <li>{WRITE_ACCESS}</li>
            </Label>
            <UserListWrapper>
              <UserEditListOutline>
                {userAccessData?.length ? (
                  userAccessData.map((access, index) => (
                    <PropelCard
                      key={access.id}
                      showClose={!access.isOwner}
                      disabled={!access.canRead && !access.canWrite}
                    >
                      {!access.isOwner && (
                        <Close
                          data-testid={`remove-button-${index}`}
                          onClick={() => handleRemoveUser(access.id)}
                        >
                          X
                        </Close>
                      )}
                      <FlexContainer>
                        <FlexBlock>{access.userName}</FlexBlock>
                        <FlexBlock>
                          <span data-testid={`access-${index}`}>
                            {getAccessType(access)}
                          </span>
                        </FlexBlock>
                        <FlexBlock>
                          <div>
                            <Checkbox
                              data-test-id="checkbox"
                              value={access.canWrite}
                              onChange={() => handleCheckboxChange(access.id)}
                              disabled={access.isOwner}
                            />
                          </div>
                        </FlexBlock>
                      </FlexContainer>
                    </PropelCard>
                  ))
                ) : (
                  <EmptyProjectPlaceholderContainer data-testid="empty-project-placeholder">
                    <EmptyProjectPlaceholderContent>
                      <div>{NO_PROJECTS_FOUND_PLACEHOLDER}</div>
                    </EmptyProjectPlaceholderContent>
                  </EmptyProjectPlaceholderContainer>
                )}
              </UserEditListOutline>
            </UserListWrapper>
          </Container>
          <ButtonContainer>
            <Button
              type="default"
              onClick={() =>
                navigate(
                  `/deliverable/user-access/ ${deliverableId}?${ACTIVE_TABS_KEY}=${USER_ACCESS}`
                )
              }
            >
              Cancel
            </Button>
            <Button
              data-test-id="user-access-save"
              type="primary"
              onClick={handleSave}
            >
              Save
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <LoaderWrapperComponent data-testid="loader">
          <BusyIndicator />
        </LoaderWrapperComponent>
      )}
    </>
  );
};
